/**
 * Достижения (ТЗ FR-GM-07, FR-GM-10).
 *
 * Достижение — отметка о пройденном рубеже, а не награда за покупку: уже
 * заработанное не отбирается и не запирается (ТЗ FR-SUB-06). Поэтому оно
 * хранится с датой получения, а не пересчитывается каждый раз: показатель, по
 * которому оно заработано, потом может просесть — сам факт остаётся.
 *
 * Список закрыт и назван здесь целиком: достижение, которое можно добавить
 * походя, обесценивает остальные.
 */

/** Что именно измеряет достижение. */
export const ACHIEVEMENT_IDS = [
  'first-thousand',
  'first-verified',
  'accuracy-95',
  'ten-words',
  'seven-days',
  'verified-35-wpm',
  'symbols-confident',
  'two-languages',
] as const;
export type AchievementId = (typeof ACHIEVEMENT_IDS)[number];

/** Описание рубежа: сколько нужно и в чём это измеряется. */
export interface AchievementDefinition {
  readonly id: AchievementId;
  /** Значение, начиная с которого рубеж считается взятым. */
  readonly target: number;
}

/**
 * Рубежи из ТЗ FR-GM-10, все до одного.
 *
 * Значения взяты оттуда же, а не подобраны: «первые 1000 символов»,
 * «точность 95%», «десять освоенных проблемных слов», «семь дней практики»,
 * «35 WPM на незнакомом тексте».
 */
export const ACHIEVEMENTS: readonly AchievementDefinition[] = Object.freeze([
  { id: 'first-thousand', target: 1000 },
  { id: 'first-verified', target: 1 },
  { id: 'accuracy-95', target: 0.95 },
  { id: 'ten-words', target: 10 },
  { id: 'seven-days', target: 7 },
  { id: 'verified-35-wpm', target: 35 },
  { id: 'symbols-confident', target: 10 },
  { id: 'two-languages', target: 2 },
]);

/** Полученное достижение. */
export interface UnlockedAchievement {
  readonly id: AchievementId;
  /** Календарная метка получения (ISO 8601). */
  readonly unlockedAt: string;
}

/** Состояние рубежа для показа: сколько уже есть и взят ли он. */
export interface AchievementStatus {
  readonly id: AchievementId;
  readonly target: number;
  /** Достигнутое значение на сейчас. */
  readonly current: number;
  /** Когда получено; null — ещё нет. */
  readonly unlockedAt: string | null;
}

/**
 * Серия занятий.
 *
 * Считается по календарным дням, а не по числу занятий: три занятия за один
 * вечер — это один день практики, и называть их серией из трёх значило бы
 * поощрять не регулярность, а усидчивость в один присест.
 */
export interface Streak {
  /** Текущая непрерывная серия, дней. */
  readonly current: number;
  /** Самая длинная серия за всё время, дней. */
  readonly longest: number;
  /** Всего дней с занятиями. */
  readonly total: number;
}

/**
 * Задание: рубеж на день или на неделю (ТЗ FR-GM-07).
 *
 * Заданий не бывает случайных: список закрыт и одинаков для всех. Случайное
 * задание невозможно ни спланировать, ни объяснить, а необъяснимая цель — это
 * уже игровой автомат, а не обучение (ТЗ FR-GM-09).
 */
export const QUEST_IDS = [
  'daily-session',
  'daily-characters',
  'daily-accuracy',
  'weekly-days',
  'weekly-characters',
  'weekly-accuracy',
] as const;
export type QuestId = (typeof QUEST_IDS)[number];

/** На какой срок считается задание. */
export type QuestPeriod = 'day' | 'week';

export interface QuestDefinition {
  readonly id: QuestId;
  readonly period: QuestPeriod;
  /** Значение, начиная с которого задание выполнено. */
  readonly target: number;
}

/**
 * Сколько символов набирается за одно занятие.
 *
 * Не круглое пожелание, а замер: пятиминутная персональная тренировка — это
 * 60 элементов, около 350 символов; занятие «Основ» — от 75 символов у
 * новичка до 546 у того, кто открыл все ряды. Триста — это «одно занятие
 * любого режима», а не «позанимайтесь подольше».
 */
export const CHARACTERS_PER_SESSION = 300;

/** Сколько дней в неделю считается регулярной практикой. */
export const DAYS_PER_WEEK_TARGET = 4;

/** Точность, при которой занятие считается чистым. */
export const CLEAN_ACCURACY = 0.95;

/**
 * Задания дня и недели.
 *
 * Недельные считаются по последним семи дням, а не по календарной неделе:
 * иначе воскресным вечером человек терял бы недельный труд из-за календаря, а
 * не из-за себя. Отнимать сделанное по звонку — ровно та механика, которую
 * запрещает ТЗ FR-GM-09.
 */
export const QUESTS: readonly QuestDefinition[] = Object.freeze([
  { id: 'daily-session', period: 'day', target: 1 },
  { id: 'daily-characters', period: 'day', target: CHARACTERS_PER_SESSION },
  { id: 'daily-accuracy', period: 'day', target: CLEAN_ACCURACY },
  { id: 'weekly-days', period: 'week', target: DAYS_PER_WEEK_TARGET },
  {
    id: 'weekly-characters',
    period: 'week',
    target: CHARACTERS_PER_SESSION * DAYS_PER_WEEK_TARGET,
  },
  { id: 'weekly-accuracy', period: 'week', target: 3 },
]);

/** Сколько дней входит в недельное задание. */
export const WEEK_DAYS = 7;

/** Состояние задания: сколько сделано и выполнено ли. */
export interface QuestStatus {
  readonly id: QuestId;
  readonly period: QuestPeriod;
  readonly target: number;
  readonly current: number;
  readonly done: boolean;
}

/**
 * Итог одного дня занятий.
 *
 * Хранится только сводка: сколько занятий, сколько символов и лучшая точность.
 * Ни нажатий, ни текстов — правило проекта о том, что сырое не сохраняется,
 * действует и здесь (ТЗ §11.2).
 */
export interface DayActivity {
  /** Календарная дата, YYYY-MM-DD. */
  readonly date: string;
  readonly sessions: number;
  readonly characters: number;
  /** Лучшая точность дня, 0..1. */
  readonly bestAccuracy: number;
}

/**
 * Сколько дней помнит журнал активности.
 *
 * Больше недели нужно, чтобы недельное задание видело всю неделю. Тридцать
 * один — длина самого длинного месяца: сезонное испытание считается по
 * календарному месяцу, и тридцать первого мая журнал не вправе забыть первое.
 * Бесконечный журнал в локальном хранилище — это счёт, который однажды
 * предъявят.
 */
export const ACTIVITY_LOG_DAYS = 31;
